import type { BoxProps } from "@chakra-ui/react";
import { Box, Button, Collapse, Grid, Icon, useDisclosure } from "@chakra-ui/react";
import { faKey } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormProvider } from "react-hook-form";
import { InputFormControl } from "../../components";
import { PasswordGenerator } from "../../components/PasswordGenerator";
import type { ResetPasswordInfo } from "../../dtos";
import { useResetPasswordForm } from "../../hooks";

export interface ResetPasswordFormProps extends BoxProps {
    isNewUser?: boolean
    userId: string
    token: string
    info: ResetPasswordInfo
    onPasswordReset: () => void
}

export const ResetPasswordForm = ({ isNewUser, userId, token, info, onPasswordReset, ...rest }: ResetPasswordFormProps) => {
    const { form, submitHandler } = useResetPasswordForm(userId, token, info, onPasswordReset)
    const { control, formState: { isSubmitting, isValid }, setValue } = form
    const { isOpen, onToggle } = useDisclosure()

    const handlePasswordGenerated = (password: string) => {
        setValue('newPassword', password)
        setValue('passwordConfirm', password, { shouldValidate: true })
    }

    return <FormProvider {...form}>
        <Box as="form" onSubmit={submitHandler} {...rest}>
            <Grid gap={4}>
                <InputFormControl label="Uživatelské jméno"
                    name="userName"
                    control={control}
                    isDisabled
                    formControlVariant="fullWidth"
                    autoComplete="username"
                    readOnly
                />

                <Box>
                    <Button
                        variant="link"
                        colorScheme="blue"
                        onClick={onToggle}
                        mb={2}
                        size="sm"
                    >
                        <Icon as={FontAwesomeIcon} icon={faKey} />
                        {isOpen ? 'Skrýt generátor hesla' : 'Použít generátor hesla'}
                    </Button>
                    <Collapse in={isOpen} animateOpacity>
                        <Box p={4} borderWidth={1} borderRadius="md" mb={4} boxShadow="sm">
                            <PasswordGenerator
                                rules={info.passwordRules || {
                                    minLenght: 8,
                                    requireDigit: true,
                                    requireLowercase: true,
                                    requireUppercase: true,
                                    requireNonLetterOrDigit: false
                                }}
                                onPasswordGenerated={handlePasswordGenerated}
                                showValidation={true}
                                autoGenerate={false}
                            />
                        </Box>
                    </Collapse>
                </Box>

                <InputFormControl label="Nové heslo"
                    type="password"
                    name="newPassword"
                    control={control}
                    isRequired
                    formControlVariant="fullWidth"
                    autoComplete="new-password"
                    aria-describedby="password-constraints"
                />

                <InputFormControl label="Potvrzení hesla"
                    type="password"
                    name="passwordConfirm"
                    control={control}
                    isRequired
                    formControlVariant="fullWidth"
                    autoComplete="new-password"
                />

                <Button colorScheme="primary" type="submit" size='lg' isLoading={isSubmitting} isDisabled={!isValid}>
                    {isNewUser ? 'Nastavit heslo' : 'Změnit heslo'}
                </Button>
            </Grid>
        </Box>
    </FormProvider>
}